export class ClientApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly fields?: Record<string, string>,
  ) {
    super(message);
    this.name = 'ClientApiError';
  }
}

interface ErrorBody {
  error?: string;
  message?: string;
  fields?: Record<string, string>;
}

async function request<T>(path: string, init: RequestInit): Promise<T> {
  let response: Response;
  try {
    response = await fetch(path, { credentials: 'same-origin', ...init });
  } catch {
    throw new ClientApiError(0, 'network', 'You appear to be offline. Check your connection and try again.');
  }

  const text = await response.text();
  let body: unknown = null;
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      body = null;
    }
  }

  if (!response.ok) {
    const parsed = (body ?? {}) as ErrorBody;
    throw new ClientApiError(
      response.status,
      parsed.error ?? 'error',
      parsed.message ?? 'Something went wrong. Please try again.',
      parsed.fields,
    );
  }

  return body as T;
}

export function apiGet<T>(path: string): Promise<T> {
  return request<T>(path, { method: 'GET' });
}

export function apiSend<T>(path: string, method: 'POST' | 'PATCH', body?: unknown): Promise<T> {
  return request<T>(path, {
    method,
    headers: { 'content-type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
}

export function apiUpload<T>(path: string, file: Blob, filename: string): Promise<T> {
  const form = new FormData();
  form.set('file', file, filename);
  return request<T>(path, { method: 'POST', body: form });
}
